import {
  chromium,
  nickel,
  manganese,
  silicon,
  sulfur,
  phosphorus,
  aluminium,
  copper,
  molybdenum,
  cobalt,
  iron,
  zinc,
  magnesium,
  carbon,
  lead,
  niobium
} from "../../../utils/periodictable";

const stainless = {
  title: "Stainless Steel",
  intro:
    "Its resistance to corrosion and staining, coupled with low maintenance and a beautiful, familiar lustre make it the ideal choice for many applications. From The Gateway Arch, spanning the Mississippi River in St. Louis, MO, to historical aircraft and automobiles like the DeLorean DMC-12 (which in some circles qualifies as both), to culinary tools, surgical tools, and even medical implants, stainless steel is the alloy of choice for many industries, especially where durability and cleanability are crucial.",
  types: [
    {
      name: "SAE 304 Stainless Steel",
      id: 0,
      abbr: "SS 304",
      description:
        "SAE 304 stainless steel, also known as A2 stainless steel is the most common type of stainless steel. Commonly used in a multitude of industrial and household applications such as machine parts, screws, automobile headers, and food-handling equipment, SAE 304 is an austenite steel, non-magnetic and not very electrically or thermally conductive. It's high resistance to corrosion, excellent atmospheric resistance, and ease of malleability makes SAE 304 a common choice for a variety of needs. We also machine Stainless Steel Type 304L, which is the same as 304 grade, but with a lower carbon content for increased weldability.",
      elements: [chromium, nickel, manganese, silicon]
    },
    {
      name: "Prodec Alloy 304/304L",
      id: 1,
      abbr: "Prodec",
      description:
        "Prodec Alloy 304/304L is an improved version of standard 304/304L stainless steel. Prodec is processed for better machinability and consistent uniformity, allowing for faster machining speeds and longer tool life, which leads to improved part quality and a lower total cost of machined parts. Prodec 304/304L is highly weldable and maintains the durable, corrosion-resistant properties of standard 304/304L, with high restance to chemicals, food, and beverages. With a tensile strength of 85ksi and a yield strength of 35ksi, Prodec is the stainless steel of choice for vacuum processing equipment and specialized parts where high-integrity is crucial.",
      elements: [chromium, nickel, manganese, silicon]
    },
    {
      name: "SAE 303 Stainless Steel",
      id: 2,
      abbr: "SS 303",
      description:
        "SAE 303 Stainless Steel, also known as A1 steel, is a free machining version of type 304. Its increased machinability is due to an addition of sulfur and phosphorus, which makes 303 able to be cut quickly and easily with a good finish. The addition of sulfur forms manganese sulfide, a soft compound that acts as a chip-breaking discontinuity and a dry lubricant to prevent built up edges on cutting tools. As a result, 303 is easier to machine quickly in large quantities, and allows for greater throughput in less time than the alternatives. For heremetic seals, high-selenium 303Se Stainless Steel must be used.",
      elements: [chromium, nickel, manganese, silicon, sulfur, phosphorus]
    },
    {
      name: "13-8 Stainless Steel / 13-8 PH",
      id: 3,
      abbr: "SS 13-8PH",
      description:
        "13-8 stainless steel is a precipitation, age hardenable stainless steel. It is extremely hard with excellent strength, good corrision resistance, and superior toughness, thanks to its low carbon content, tight chemical composition control, and vacuum melting. 13-8 stainless is commonly used in aerospace for large airframe structures and aircraft components such as landing gear, valves, and shafts.",
      elements: [chromium, nickel, molybdenum, aluminium]
    },
    {
      name: "15-5 Stainless Steel",
      id: 4,
      abbr: "SS 15-5",
      description:
        "15-5 stainless steel is a variant of the older 17-4 chromium-nickel-copper preciptitation hardening stainless steel. Like 17-4, 15-5 has high strength, maintained to approximately 600° F, moderate corrosion resistance, and higher toughness than 17-4, achieved by a reduced delta ferrite content. 15-5 is commonly used for aircraft components, such as shafts, fasteners, fittings and gears, as well as high pressure corrosive parts such as valves.",
      elements: [chromium, nickel, copper, manganese, silicon]
    },
    {
      name: "17-4 Stainless Steel",
      id: 5,
      abbr: "SS 17-4PH",
      description:
        "17-4 Stainless Steel, also known as type 630, is a precipitation hardened stainless steel. 17-4 can be heat treated to high levels of strength and hardness, and is both corrosion-resistant and highly machinable. As a martensitic steel, it is also magnetic. 17-4 is commonly used in aerospace for a variety of aircraft parts which include components that require high hardness at temperatures up to 600° F.",
      elements: [chromium, nickel, copper, manganese, silicon]
    }
  ]
};

const exotics = {
  title: "Exotic Metals",
  intro:
    "When a part needs to withstand the most extreme environments imaginable—both on and off Earth—it's essential to use exotic metals and superalloys like Inconel, with its absolute-zero thermal expansion over an extreme temperature range. These exotic and often difficult-to-machine metals are our specialty at Accratronics, where our exotic parts have found themselves all across the galaxy, from the depths of the oceans to the surface of the moon.",
  types: [
    {
      name: "Inconel 718",
      id: 0,
      abbr: "Inconel",
      description:
        "Inconel Alloy 718 is a nickel-based superalloy designed for exceptionally high yield, tensile and creep-rupture properties at temperatures up to 1300° F. Oxidation and corrosion reistant, when heated, Inconel forms a thick and stable passivating oxide layer protecting the surface from further attack. Inconel is often found in extreme environments, being commonly used in gas turbines, high-temperature fasteners, nuclear pressurized water reactors, firearm suppressors, and F1 & Nascar exhaust systems. Despite its lofty applications, it's been found in consumer grade products as well, like inside the turbo system of the Mazda RX-7, or the exhaust system of Norton motorcycles where exhaust temperatures can exceed 1,000° C. In aerospace, Inconel has been used to secure rocket boosters to the launch platform on the Space Shuttle, with eight Inconel studs in total supporting the entire shuttle. It's been used to construct the skin of the X-15 Rocket-powered aircraft, and inside the thrust chamber of the F-1 rocket engine in the Saturn V booster. Typically, the inconel used to make hermetic seals is Inconel 718—gamma double prime strenghtened and highly weldable.",
      elements: [nickel, chromium, niobium, molybdenum, cobalt, iron]
    },
    {
      name: "Nitronic 50",
      id: 1,
      abbr: "Nitronic",
      description:
        "Nitronic 50 Stainless Steel provides a combination of corrosion resistance and strength not found in any other commercial material available in its price range. Nitronic 50's exceptionally low magnetic permeability and outstanding corrosion resistance make it the leading alloy for applications where 316 and 317 just won't do. Suitable for chemical, petrochemical, nuclear fuel recycling, petroleum, fertilizer, textile, marine and food processing industries, Nitronic 50 is an excellent choice for hermetic seals, pumps, valves and fittings, cables, fasteners, pump shafts, and countless other applications.",
      elements: [nickel, chromium, molybdenum, manganese, silicon]
    },
    {
      name: "Kovar™",
      id: 2,
      abbr: "Kovar™",
      description:
        "Kovar™ is a nickel-cobalt alloy with the same thermal expansion characteristics as borosilicate glass, which makes it an ideal choice for parts requiring a glass-to-metal seal. In fact, Kovar™ was invented for just that reason. Not only does it have a similiar thermal expansion to glass, but its expansion curve can be made to match that of the glass it will bond with, allowing for the seal to cool without placing any added stress on the joint. Kovar™ is often found in vacuum tubes, cathode ray tubes, X-ray tubes, and lightbulbs.",
      elements: [iron, nickel, cobalt, silicon, manganese]
    },
    {
      name: "Iron-Nickel Alloy",
      id: 3,
      abbr: "Iron-Nickel Alloy",
      description:
        "Iron-Nickel Alloy, previously known as Alloy 52 or 52 Alloy, is a controlled-expansion alloy developed specifically for glass-to-metal seals. It is more durable than nickel itself, and it's low thermal expansion properties make it highly suited for use in a variety of applications in the aerospace industry. Its near zero coefficient of thermal expansion and high strength and toughness—even at cryogenic temperatures—make it a great choice for electronic tubes, lamps, and hermetic devices.",
      elements: [nickel, iron, silicon]
    }
  ]
};

const aluminumAlloys = {
  title: "Aluminum Alloys",
  intro:
    "Lightweight, abundant, and non-magnetic, aluminum is the most widely used non-ferrous metal on Earth. Making up 75%-80% of the parts in a modern aircraft, aluminum alloys have been an important part of aerospace manufacturing since the introduction of the first metal-skinned aircraft in the 1915, the Junkers J1, a World War I German monoplane which—unlike other aircraft of the era which relied on fabric-covered wooden structures—featured an aluminum alloy fuselage and steel-paneled wings. \n\nToday, aluminum is used for the fuselage, wing panes, rudder, exhaust pipes, doors, floors, seats, turbines and cockpit instrumentation of nearly every aircraft in flight. Modern spacecraft are primarily comprised of aluminum, with alloys used on the Apollo, Skylab, Space Shuttles and International Space Station, with the currently-under-development Orion spacecraft—intended to allow human exploration of Mars—having its main structural components made of an aluminum lithium alloy. To say aerospace runs on aluminum is an understatement.",
  types: [
    {
      name: "6061 Aluminum Alloy",
      subName: "CF-6061 T-4 / CF-6061 T-6 / CF-6061 T-651 / EXT-6061 T-6511",
      id: 0,
      abbr: "6061",
      description:
        'Aluminum Alloy 6061, originally called "Alloy 61S", is the second most popular alloy in extrusions, and one of the most popular alloys for general use. We machine tempered and pre-tempered grades of 6061, including 6061-O (annealed), 6061-T6, and 6061-T4.\r6061-O is easily worked and corrosion resistant, with a maximum tensile strength of no more than 18,000 psi, a maximum yield strength of no more than 8,000 psi, and elongation of 25%-30%. It is commonly used in aircraft structures such as wings and fuselages, automobile chassis, modern SCUBA tanks, and even food &amp; beverage cans.\n\n6061-T4 has an ultimate tensile strength of at least 30,000 psi, a yield strength of 16,000 psi, and an elongation of 16%.\n\n6061-T6 has an ultimate tensile strength of at least 42,000 psi, a yield strength of at least 35,000 psi, and elongation of 8%-10%. 6061-T6 is commonly found in bicycle frames and components, fly fishing reels, firearm sound suppressors, and ultra-high vacuum chambers.',
      elements: [aluminium, zinc, magnesium, copper]
    },
    {
      name: "2024 Aluminum Alloy",
      subName: "CF-2024 T-4 / CF-2024 T-351 / CF-2024 T-851 / EXT-2024 T-3511",
      id: 1,
      abbr: "2024",
      description:
        "2024, originally named 24ST, is an aluminum alloy with copper as the primary alloying element. It has an excellent strength to weight ratio, with good fatigue resistance. 2024 is the most commonly extruded alloy and is widely used in aircraft structures that will be under tension, such as wings and fuselages.",
      elements: [aluminium, manganese, magnesium, copper]
    },
    {
      name: "7075 Aluminum Alloy",
      subName:
        "CF-7075 T-6 / CF-7075 T-651 / EXT-7075 T-6511 / CF-7075 T-73 / CF-7075 T-7351 / EXT-7075 T-73511",
      id: 2,
      abbr: "7075",
      description:
        "7075 is an aluminum alloy, primarily alloyed with zinc. 7000-series aluminum alloy is often used in marine transport, automotive and avation because of its high strength-to-density ratio. 7075 is commonly used for rock climbing equipment, bicycle components, assault rifles, and lacrosse sticks.",
      elements: [iron, nickel, cobalt, silicon, manganese]
    }
  ]
};

const steel = {
  title: "Steel",
  intro:
    "America was built on steel. Hard, ductile, and strong, there's a good reason why this metal has been the building block for construction for the entirety of the modern age.",
  types: [
    {
      name: "1215 Cold Roll Steel",
      id: 0,
      abbr: "1215",
      description:
        "1215 is a free machining steel, commonly referred to as screw machine stock. Well-suited for automatic screw machine operations, 1215 is ideal for parts that require complex machining and close finish tolerances with a bright, smooth finish, but it is also suitable for parts such as bushings, inserts, couplings, studs, pins and more.",
      elements: [carbon, iron, manganese, phosphorus, sulfur]
    },
    {
      name: "12L14 Cold Roll Steel",
      id: 1,
      abbr: "12L14",
      description:
        "12L14 is used extensively in automatic screw machine operations that require complex machining and close finish tolerances, such as bushings, inserts, couplings and hydraulic hose fittings. With good ductility, 12L14 is suitable for parts involving crimping, bending, or riveting.",
      elements: [carbon, iron, lead, manganese, phosphorus, sulfur]
    }
  ]
};

export { stainless, exotics, aluminumAlloys, steel };



// WEBPACK FOOTER //
// ./src/pages/Materials/MaterialComponent/materialslist.js